<template>
  <ValidationObserver ref="observer" v-slot="{ invalid }">
    <vx-card class="mt-5">
      <div class="p-6 invoice-detail">
        <h3>#{{ form.docNo }}</h3>
        <div class="flex flex-wrap-reverse flex-grow justify-end">
          <div class="flex-wrap-reverse">
            <vs-button
              class="mr-3"
              :disabled="!isValidated || isDownloadingPdf"
              :loading="isDownloadingPdf"
              @click="downloadEInvoicePdf"
              >{{ isDownloadingPdf ? $t('dashboard.downloading') : $t('dashboard.downloadEInvoice') }}</vs-button
            >
          </div>
          <!-- Show either Submit to LHDN or Submit to SG Peppol based on enableSgPeppol -->
          <div v-if="!isSgPeppolEnabled" class="flex-wrap-reverse">
            <vs-button
              class="mr-3"
              :disabled="!isValidToSubmitStatus(form.eInvoice.status)"
              @click="submitToLhdn"
              >{{ $t('dashboard.submitToLhdn').toUpperCase() }}
            </vs-button>
          </div>
          <!-- SG Peppol Submit Button - Only shown when enableSgPeppol is true -->
          <div v-else class="flex-wrap-reverse">
            <vs-button
              class="mr-3"
              color="success"
              :disabled="!isValidToSubmitIrsp"
              @click="submitToIrsp"
              >{{ $t('dashboard.submitToSgPeppol').toUpperCase() }}
            </vs-button>
          </div>
          <div class="flex-wrap-reverse" v-if="!isSgPeppolEnabled">
            <vs-button @click="showIrbmPayload = true">{{ $t('dashboard.viewPayload') }}</vs-button>
          </div>
        </div>

        <!-- Overview Info -->
        <vx-card class="mt-base" :title="$t('dashboard.overview')" no-shadow card-border>
          <div class="pr-3 pl-3 vx-row">
            <div class="w-full md:w-1/2 sm:w-1/2">
              <label class="text-sm text-black">{{ $t('dashboard.invoiceNo') }}:</label><br />
              <p>{{ form.docNo }}</p>
            </div>
            <div class="w-full md:w-1/2 sm:w-1/2" v-if="!isSgPeppolEnabled">
              <label class="text-sm text-black">{{ $t('dashboard.irbmNo') }}:</label><br />
              <p>{{ form.eInvoice.lhdnEInvoiceUuid }}</p>
            </div>
            <div class="w-full md:w-1/2 sm:w-1/2" v-else>
              <label class="text-sm text-black">{{ $t('dashboard.acknowledgementId') }}:</label><br />
              <p>{{ form.eInvoice.irspAcknowledgementId || 'N/A' }}</p>
            </div>
            <div class="w-full md:w-1/2 sm:w-1/2">
              <label class="text-sm text-black">{{ $t('dashboard.status') }}:</label><br />
              <vs-chip
                :color="getEInvoiceStatusColor(form.eInvoice.status)"
                class="product-order-status"
              >
                {{ getLocalizedEInvoiceStatus(form.eInvoice.status) }}
              </vs-chip>
            </div>
            <div class="mt-3 w-full md:w-1/2 sm:w-1/2">
              <label class="text-sm text-black">{{ $t('dashboard.eInvoiceType') }}:</label><br />
              <p>{{ getType(form.eInvoice.type) }}</p>
            </div>
            <div class="mt-3 w-full md:w-1/2 sm:w-1/2">
              <label class="text-sm text-black">{{ $t('dashboard.total') }}:</label><br />
              <p class="text-bold">
                {{
                  form.total | toCurrency(form.currency ? form.currency : '')
                }}
              </p>
              <p class="text-sm text-black">
                {{ $t('dashboard.exchangeRate') }}:
                {{ form.exchange?.rate ? form.exchange?.rate : '1.00' }}
              </p>
            </div>
            <div class="w-full md:w-1/2 sm:w-1/2" v-if="!isSgPeppolEnabled">
              <label class="text-sm text-black">{{ $t('dashboard.eInvoiceVersion') }}:</label><br />
              <p>{{ form.eInvoice.invoiceVersion }}</p>
            </div>
            <div class="w-full md:w-1/2 sm:w-1/2" v-else>
              <label class="text-sm text-black">{{ $t('dashboard.destination') }}:</label><br />
              <p>{{ form.eInvoice.irspDestination || 'N/A' }}</p>
            </div>
            <div class="mt-3 w-full md:w-1/2 sm:w-1/2">
              <label class="text-sm text-black">{{ $t('dashboard.eInvoiceSubmittedDate') }}:</label
              ><br />
              <p>
                {{ (isSgPeppolEnabled ? form.eInvoice.irspSubmittedAt : form.eInvoice.invoiceDate) ? $moment(isSgPeppolEnabled ? form.eInvoice.irspSubmittedAt : form.eInvoice.invoiceDate).format('DD MMM YYYY, hh:mm A') : 'N/A' }}
              </p>
            </div>
            <div class="mt-3 w-full md:w-1/2 sm:w-1/2" v-if="!isSgPeppolEnabled">
              <label class="text-sm text-black">{{ $t('dashboard.eInvoiceValidationUrl') }}:</label
              ><br />
              <a v-bind:href="form.eInvoice.validationLink" target="_blank">{{
                form.eInvoice.validationLink
              }}</a>
            </div>
            <div class="mt-3 w-full md:w-1/2 sm:w-1/2" v-if="!isSgPeppolEnabled">
              <label class="text-sm text-black">{{ $t('dashboard.eInvoiceValidatedDate') }}:</label
              ><br />
              <p>
                {{ form.eInvoice.validationDate ? $moment(form.eInvoice.validationDate).format('DD MMM YYYY, hh:mm A') : 'N/A' }}
              </p>
            </div>
            <div class="mt-3 w-full md:w-1/2 sm:w-1/2" v-if="isSgPeppolEnabled">
              <label class="text-sm text-black">{{ $t('dashboard.sgPeppolStatus') }}:</label><br />
              <p>{{ form.eInvoice.irspPeppolStatus || 'N/A' }}</p>
            </div>
            <div class="mt-3 w-full md:w-1/2 sm:w-1/2" v-if="isSgPeppolEnabled">
              <label class="text-sm text-black">{{ $t('dashboard.irasStatus') }}:</label><br />
              <p>{{ form.eInvoice.irspIrasStatus || 'N/A' }}</p>
            </div>
            <div class="mt-3 w-full md:w-1/2 sm:w-1/2" v-if="isSgPeppolEnabled && form.eInvoice.irspBuyerResponse">
              <label class="text-sm text-black">{{ $t('dashboard.buyerResponse') }}:</label><br />
              <vs-chip :color="buyerResponseColor">{{ form.eInvoice.irspBuyerResponse }}</vs-chip>
              <span v-if="form.eInvoice.irspBuyerResponseAt" class="text-xs text-gray-500 ml-1">
                {{ $moment(form.eInvoice.irspBuyerResponseAt).format('DD MMM YYYY, hh:mm A') }}
              </span>
            </div>
            <div class="mt-3 w-full md:w-1/2 sm:w-1/2" v-if="form.description">
              <label class="text-sm text-black">{{ $t('dashboard.description') }}:</label><br />
              <p>
                {{ form.description }}
              </p>
            </div>
          </div>
        </vx-card>
        <!-- End Overview Info -->

        <!--Local Validation-->
        <vx-card class="mt-5">
          <vs-table
            stripe
            ref="table"
            v-if="localValidationErrors"
            :data="localValidationErrors"
            :noDataText="$t('dashboard.noDataAvailable')"
          >
            <template slot="thead">
              <vs-th>{{ $t('dashboard.missingItems') }}</vs-th>
            </template>
            <template slot-scope="{ data }">
              <tbody>
                <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
                  <vs-td>
                    <p>❗ {{ tr.message }}</p>
                  </vs-td>
                </vs-tr>
              </tbody>
            </template>
          </vs-table>
          <!-- Contact Error Summary -->
          <div v-if="contactErrorSummary && contactErrorSummary.length" class="mb-4 p-4 bg-warning-lightest border-l-4 border-warning text-gray-800">
            <div class="flex items-start gap-3">
              <ul class="mb-0 list-disc list-inside contact-error-list flex-1">
                <li v-for="(msg, idx) in contactErrorSummary" :key="idx">
                 ❗ {{ msg }}
                </li>
              </ul>
            </div>
          </div>
        </vx-card>
        <!--        LHDN Validation (Malaysia only — SG Peppol uses IRSP/PINT validation) -->
        <vx-card class="mt-5" v-if="!isSgPeppolEnabled">
          <vs-table
            stripe
            ref="table"
            v-if="lhdnValidationErrors && lhdnValidationErrors.length > 0"
            :data="lhdnValidationErrors"
            :noDataText="$t('dashboard.noDataAvailable')"
          >
            <template slot="thead">
              <vs-th>{{ $t('dashboard.submissionErrors') }}</vs-th>
            </template>
            <template slot-scope="{ data }">
              <tbody>
                <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
                  <vs-td>
                    <p>❗ {{ tr.name }}</p>
                  </vs-td>
                  <vs-td>
                    <p>
                      {{
                        JSON.stringify(tr.error.innerError).replace(
                          /,/gm,
                          `,\n`
                        )
                      }}
                    </p>
                  </vs-td>
                </vs-tr>
              </tbody>
            </template>
          </vs-table>
        </vx-card>
        <!-- Cancellation Response (LHDN only) -->
        <vx-card
          v-if="
            !isSgPeppolEnabled &&
            form.eInvoice.cancellationResponse &&
            form.eInvoice.status === 'cancelled'
          "
          class="mt-5"
        >
          <div>
            <span
              >{{ $t('dashboard.cancellationResponse') }}:
              {{
                JSON.stringify(form.eInvoice.cancellationResponse).replace(
                  /,/gm,
                  `,\n`
                )
              }}</span
            >
          </div>
        </vx-card>

        <!-- Cancellation Reason (LHDN only) -->
        <vx-card
          v-if="
            !isSgPeppolEnabled &&
            form.eInvoice.cancellationReason &&
            form.eInvoice.status === 'cancelled'
          "
          class="mt-5"
        >
          <div>
            <span
              >{{ $t('dashboard.cancellationReason') }}:
              {{
                JSON.stringify(form.eInvoice.cancellationReason).replace(
                  /,/gm,
                  `,\n`
                )
              }}</span
            >
          </div>
        </vx-card>

        <!-- Supplier Info -->
        <vx-card class="mt-base" :title="$t('dashboard.supplier')" no-shadow card-border>
          <v-card-actions>
            <v-btn
              color="orange-lighten-2"
              variant="text"
              @click="showSupplier = !showSupplier"
            >
              {{
                showSupplier
                  ? $t('dashboard.hideSupplierDetails')
                  : $t('dashboard.expandSupplierDetails')
              }}
            </v-btn>
          </v-card-actions>

          <v-expand-transition>
            <div v-show="showSupplier">
              <v-divider></v-divider>

              <vx-card class="mt-base" no-shadow card-border>
                <div class="pr-3 pl-3 vx-row">
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.supplierName')"
                      v-model="form.supplier.name"
                      class="w-full pr-5 {'input':true}"
                      name="supplierName"
                      v-validate="'required'"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('supplierName')"
                      >{{ errors.first('supplierName') }}</span
                    >
                  </div>
                  <!-- LHDN-only supplier identity (TIN/SST/MSIC/Tourism). Hidden for
                       SG Peppol tenants, who are identified by Peppol ID / UEN / GST. -->
                  <template v-if="!isSgPeppolEnabled">
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.supplierTin')"
                      v-model="form.supplier.tin"
                      class="pr-5 w-full"
                      name="supplierTin"
                      v-validate="'required'"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('supplierTin')"
                      >{{ errors.first('supplierTin') }}</span
                    >
                  </div>
                  <div class="flex pr-3 pl-3 mt-3 w-full">
                    <div style="width: 50%">
                      <label class="text-sm"
                        >Registration Type / No
                        <span class="text-error">*</span>
                      </label>
                    </div>
                    <div style="width: 50%">
                      <label class="text-sm">Supplier SST</label>
                    </div>
                  </div>
                  <div class="flex mt-1 w-9/12 md:w-1/2 sm:w-1/2">
                    <v-select
                      :options="CustomerRegistrationTypes"
                      lebel="label"
                      name="supplierregistrationType"
                      :reduce="
                        (supplierregistrationType) =>
                          supplierregistrationType.label
                      "
                      v-model="form.supplier.registrationType"
                      style="width: 40%"
                      class="mr-3"
                     @option:selected="supplierRegistrationTypeUpdate"
                    />
                    <vs-input
                      v-model="form.supplier.registration"
                      style="width: 55%"
                      class="mr-5"
                      name="supplierRegistration"
                      v-validate="'required'"
                    />
                    <!-- <span
                      class="text-sm text-danger"
                      v-show="errors.has('supplierRegistration')"
                      >{{ errors.first('supplierRegistration') }}</span
                    > -->
                  </div>
                  <div class="mt-1 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      v-model="form.supplier.sst"
                      class="pr-5 w-full"
                      name="supplierSST"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('supplierSST')"
                      >{{ errors.first('supplierSST') }}</span
                    >
                  </div>
                  <div class="mt-8 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-button color="success" type="border" class="ml-5">
                        <div @click="searchTin('supplier')">Search Tin</div>
                      </vs-button>
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.supplierTourismRegistration')"
                      v-model="form.supplier.tourismRegistration"
                      class="pr-5 w-full"
                      name="supplierTourismRegistration"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('supplierTourismRegistration')"
                      >{{ errors.first('supplierTourismRegistration') }}</span
                    >
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.supplierMsicCode')"
                      v-model="form.supplier.msic"
                      class="pr-5 w-full"
                      name="supplierMSIC"
                      v-validate="'required'"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('supplierMSIC')"
                      >{{ errors.first('supplierMSIC') }}</span
                    >
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.supplierBusinessActivity')"
                      v-model="form.supplier.activityDesc"
                      class="pr-5 w-full"
                      name="supplierActivityDesc"
                      v-validate="'required'"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('supplierActivityDesc')"
                      >{{ errors.first('supplierActivityDesc') }}</span
                    >
                  </div>
                  </template>
                  <!-- SG Peppol supplier identity (read-only, sourced from tenant
                       sgPeppolSettings — not the LHDN-shaped billing-doc supplier). -->
                  <template v-else>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.peppolId')"
                      :value="sgPeppolSettings.peppolId || 'N/A'"
                      class="pr-5 w-full"
                      readonly
                    />
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.sgUen')"
                      :value="sgPeppolSettings.uen || 'N/A'"
                      class="pr-5 w-full"
                      readonly
                    />
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.gstRegistrationNo')"
                      :value="sgPeppolSettings.gstRegistrationNo || 'N/A'"
                      class="pr-5 w-full"
                      readonly
                    />
                  </div>
                  </template>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.supplierEmail')"
                      v-model="form.supplier.email"
                      class="pr-5 w-full"
                      name="supplierEmail"
                      v-validate="'required|email'"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('supplierEmail')"
                      >{{ errors.first('supplierEmail') }}</span
                    >
                  </div>
                  <!--            Address Section-->
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.supplierAddressNum')"
                      v-model="form.supplier.address.addressNum"
                      class="pr-5 w-full"
                      name="supplierAddress.addressNum"
                      v-validate="'required'"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('supplierAddress.addressNum')"
                      >{{ errors.first('supplierAddress.addressNum') }}</span
                    >
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.supplierAddressLine1')"
                      v-model="form.supplier.address.addressLine1"
                      class="pr-5 w-full"
                      name="supplierAddress.addressLine1"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('supplierAddress.addressLine1')"
                      >{{ errors.first('supplierAddress.addressLine1') }}</span
                    >
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.supplierAddressLine2')"
                      v-model="form.supplier.address.addressLine2"
                      class="pr-5 w-full"
                      name="supplierAddress.addressLine2"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('supplierAddress.addressLine2')"
                      >{{ errors.first('supplierAddress.addressLine2') }}</span
                    >
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.supplierAddressPostal')"
                      v-model="form.supplier.address.addressPostal"
                      class="pr-5 w-full"
                      name="supplierAddress.addressPostal"
                      v-validate="'required|max:10'"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('supplierAddress.addressPostal')"
                      >{{ errors.first('supplierAddress.addressPostal') }}</span
                    >
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.supplierAddressCity')"
                      v-model="form.supplier.address.addressCity"
                      class="pr-5 w-full"
                      name="supplierAddress.addressCity"
                      v-validate="'required'"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('supplierAddress.addressCity')"
                      >{{ errors.first('supplierAddress.addressCity') }}</span
                    >
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <label class="text-sm opacity-75"> Supplier State *</label>
                    <v-select
                      :options="states"
                      :reduce="(myState) => myState.code"
                      v-if="form.supplier.address.addressCountryCode == 'MY'"
                      v-model="form.supplier.address.addressState"
                      class="pr-5 w-full"
                      name="supplierAddress.addressState"
                      v-validate="'required'"
                    />
                    <!-- to display text for Other Countries -->
                    <vs-input
                      v-model="form.supplier.address.addressState"
                      v-if="form.supplier.address.addressCountryCode != 'MY'"
                      class="pr-5 w-full"
                      name="supplierAddress.addressState"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('supplierAddress.addressState')"
                      >{{ errors.first('supplierAddress.addressState') }}</span
                    >
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <label class="text-sm opacity-75">
                      Supplier Country *</label
                    >
                    <v-select
                      :options="countries"
                      :reduce="(country) => country.code"
                      v-model="form.supplier.address.addressCountryCode"
                      class="pr-5 w-full"
                      v-validate="'required'"
                      @option:selected="stateUpdate('supplier')"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('supplierAddress.addressCountryCode')"
                      >{{
                        errors.first('supplierAddress.addressCountryCode')
                      }}</span
                    >
                  </div>
                  <!--            End of Address Section-->
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.supplierContactNumber')"
                      v-model="form.supplier.contact"
                      class="pr-5 w-full"
                      name="supplierContact"
                      v-validate="'required|phone'"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('supplierContact')"
                      >{{ errors.first('supplierContact') }}</span
                    >
                  </div>
                </div>
              </vx-card>
            </div>
          </v-expand-transition>
        </vx-card>
        <!-- Buyer Info -->
        <vx-card class="mt-base" :title="$t('dashboard.buyer')" no-shadow card-border>
          <v-card-actions>
            <v-btn
              color="orange-lighten-2"
              variant="text"
              @click="showBuyer = !showBuyer"
            >
              {{ showBuyer ? $t('dashboard.hideBuyerDetails') : $t('dashboard.expandBuyerDetails') }}
            </v-btn>
          </v-card-actions>

          <v-expand-transition>
            <div v-show="showBuyer">
              <v-divider></v-divider>
              <vx-card class="mt-base" :title="$t('dashboard.buyer')" no-shadow card-border>
                <div class="pr-3 pl-3 vx-row">
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.buyerName')"
                      v-model="form.buyer.name"
                      class="pr-5 w-full"
                      name="buyerName"
                      v-validate="'required'"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('buyerName')"
                      >{{ errors.first('buyerName') }}</span
                    >
                  </div>
                  <!-- LHDN-only buyer identity (TIN/Registration Type/SST). Hidden for
                       SG Peppol tenants; SG buyers are identified by UEN / Peppol ID. -->
                  <template v-if="!isSgPeppolEnabled">
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.buyerTin')"
                      v-model="form.buyer.tin"
                      class="pr-5 w-full"
                      name="buyerTin"
                      v-validate="'required'"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('buyerTin')"
                      >{{ errors.first('buyerTin') }}</span
                    >
                  </div>
                  <div class="flex mt-3 w-full">
                    <div style="width: 50%">
                      <label class="text-sm"
                        ><span class="text-error">*</span> Registration Type /
                        No</label
                      >
                    </div>
                    <div style="width: 50%">
                      <label class="text-sm"
                        ><span class="text-error">*</span> Buyer SST</label
                      >
                    </div>
                  </div>
                  <div class="flex mt-1 w-9/12 md:w-1/2 sm:w-1/2">
                    <v-select
                      :options="CustomerRegistrationTypes"
                      lebel="label"
                      name="buyerregistrationType"
                      :reduce="
                        (buyerregistrationType) => buyerregistrationType.label
                      "
                      v-model="form.buyer.registrationType"
                      class="mr-3"
                      style="width: 50%"
                      @option:selected="buyerRegistrationTypeUpdate"
                    />
                    <vs-input
                      v-model="form.buyer.registration"
                      class="mr-5 w-full"
                      name="buyerRegistration"
                      v-validate="'required'"
                    />
                  </div>
                  <div class="flex mt-1 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      v-model="form.buyer.sst"
                      class="w-full"
                      name="buyerSST"
                    />
                  </div>
                  <div class="flex w-full">
                    <div style="width: 50%">
                      <span
                        class="text-sm text-danger"
                        v-show="errors.has('buyerRegistration')"
                        >{{ errors.first('buyerRegistration') }}</span
                      >
                    </div>
                    <div style="width: 50%">
                      <span
                        class="text-sm text-danger"
                        v-show="errors.has('buyerSST')"
                        >{{ errors.first('buyerSST') }}</span
                      >
                    </div>
                  </div>
                  <div class="mt-8 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-button color="success" type="border" class="ml-5">
                        <div @click="searchTin('buyer')">Search Tin</div>
                      </vs-button>
                  </div>
                  </template>
                  <!-- SG Peppol buyer identity (read-only). SG buyers are identified
                       by their Singapore UEN / Peppol participant ID, sourced from the
                       linked customer record — not the LHDN TIN/SST/registration. -->
                  <template v-else>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.sgUen')"
                      :value="buyerSgUen || 'N/A'"
                      class="pr-5 w-full"
                      readonly
                    />
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.peppolId')"
                      :value="buyerPeppolId || 'N/A'"
                      class="pr-5 w-full"
                      readonly
                    />
                  </div>
                  </template>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.buyerEmail')"
                      v-model="form.buyer.email"
                      class="pr-5 w-full"
                      name="buyerEmail"
                      v-validate="'required|email'"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('buyerEmail')"
                      >{{ errors.first('buyerEmail') }}</span
                    >
                  </div>
                  <!--            Address Section-->
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.buyerAddressNum')"
                      v-model="form.buyer.address.addressNum"
                      class="pr-5 w-full"
                      name="buyerAddress.addressNum"
                      v-validate="'required'"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('buyerAddress.addressNum')"
                      >{{ errors.first('buyerAddress.addressNum') }}</span
                    >
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.buyerAddressLine1')"
                      v-model="form.buyer.address.addressLine1"
                      class="pr-5 w-full"
                      name="buyerAddress.addressLine1"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('buyerAddress.addressLine1')"
                      >{{ errors.first('buyerAddress.addressLine1') }}</span
                    >
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.buyerAddressLine2')"
                      v-model="form.buyer.address.addressLine2"
                      class="pr-5 w-full"
                      name="buyerAddress.addressLine2"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('buyerAddress.addressLine2')"
                      >{{ errors.first('buyerAddress.addressLine2') }}</span
                    >
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.buyerAddressPostal')"
                      v-model="form.buyer.address.addressPostal"
                      class="pr-5 w-full"
                      name="buyerAddress.addressPostal"
                      v-validate="'required|max:10'"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('buyerAddress.addressPostal')"
                      >{{ errors.first('buyerAddress.addressPostal') }}</span
                    >
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.buyerAddressCity')"
                      v-model="form.buyer.address.addressCity"
                      class="pr-5 w-full"
                      name="buyerAddress.addressCity"
                      v-validate="'required'"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('buyerAddress.addressCity')"
                      >{{ errors.first('buyerAddress.addressCity') }}</span
                    >
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <label class="text-sm opacity-75">
                      Buyer Address State *</label
                    >
                    <v-select
                      :options="states"
                      :reduce="(myState) => myState.code"
                      v-if="form.buyer.address.addressCountryCode == 'MY'"
                      v-model="form.buyer.address.addressState"
                      class="pr-5 w-full"
                      name="buyerAddress.addressState"
                      v-validate="'required'"
                    />
                    <!-- to display text for Other Countries -->
                    <vs-input
                      v-model="form.buyer.address.addressState"
                      v-if="form.buyer.address.addressCountryCode != 'MY'"
                      class="pr-5 w-full"
                      name="buyerAddress.addressState"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('buyerAddress.addressState')"
                      >{{ errors.first('buyerAddress.addressState') }}</span
                    >
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <label class="text-sm opacity-75"> Buyer Country *</label>
                    <v-select
                      :options="countries"
                      :reduce="(country) => country.code"
                      v-model="form.buyer.address.addressCountryCode"
                      class="pr-5 w-full"
                      v-validate="'required'"
                      @option:selected="stateUpdate('buyer')"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('buyerAddress.addressCountryCode')"
                      >{{
                        errors.first('buyerAddress.addressCountryCode')
                      }}</span
                    >
                  </div>
                  <!--            End of Address Section-->
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.buyerContactNumber')"
                      v-model="form.buyer.contact"
                      class="pr-5 w-full"
                      name="buyerContact"
                      v-validate="'required|phone'"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('buyerContact')"
                      >{{ errors.first('buyerContact') }}</span
                    >
                  </div>

                  <div
                  v-if="isSgPeppolEnabled"
                  class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      label="Buyer Contact Name"
                      v-model="form.recipient.name"
                      class="pr-5 w-full"
                      name="recipientName"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('recipientName')"
                      >{{ errors.first('recipientName') }}</span
                    >
                  </div>

                </div>
              </vx-card>
            </div>
          </v-expand-transition>
        </vx-card>
        <!-- Shipping Recipient Info (LHDN Delivery party — not used in SG Peppol / PINT-SG) -->
        <vx-card
          v-if="!isSgPeppolEnabled"
          class="mt-base"
          :title="$t('dashboard.recipient')"
          no-shadow
          card-border
        >
          <v-card-actions>
            <v-btn
              color="orange-lighten-2"
              variant="text"
              @click="showRecipient = !showRecipient"
            >
              {{
                showRecipient
                  ? $t('dashboard.hideRecipientDetails')
                  : $t('dashboard.expandRecipientDetails')
              }}
            </v-btn>
          </v-card-actions>

          <v-expand-transition>
            <div v-show="showRecipient">
              <v-divider></v-divider>
              <vx-card class="mt-base" title="Recipient" no-shadow card-border>
                <div class="pr-3 pl-3 vx-row">
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.recipientName')"
                      v-model="form.recipient.name"
                      class="pr-5 w-full"
                      name="recipientName"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('recipientName')"
                      >{{ errors.first('recipientName') }}</span
                    >
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.recipientTin')"
                      v-model="form.recipient.tin"
                      class="pr-5 w-full"
                      name="recipientTin"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('recipientTin')"
                      >{{ errors.first('recipientTin') }}</span
                    >
                  </div>
                  <div class="flex mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.recipientRegistration')"
                      v-model="form.recipient.registration"
                      class="pr-5 w-full"
                      name="recipientRegistration"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('recipientRegistration')"
                      >{{ errors.first('recipientRegistration') }}</span
                    >
                  </div>
                  <!--            Address Section-->
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.recipientAddressNum')"
                      v-model="form.recipient.address.addressNum"
                      class="pr-5 w-full"
                      name="recipientAddress.addressNum"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('recipientAddress.addressNum')"
                      >{{ errors.first('recipientAddress.addressNum') }}</span
                    >
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.recipientAddressLine1')"
                      v-model="form.recipient.address.addressLine1"
                      class="pr-5 w-full"
                      name="recipientAddress.addressLine1"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('recipientAddress.addressLine1')"
                      >{{ errors.first('recipientAddress.addressLine1') }}</span
                    >
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.recipientAddressLine2')"
                      v-model="form.recipient.address.addressLine2"
                      class="pr-5 w-full"
                      name="recipientAddress.addressLine2"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('recipientAddress.addressLine2')"
                      >{{ errors.first('recipientAddress.addressLine2') }}</span
                    >
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.recipientAddressPostal')"
                      v-model="form.recipient.address.addressPostal"
                      class="pr-5 w-full"
                      name="recipientAddress.addressPostal"
                      v-validate="'max:10'"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('recipientAddress.addressPostal')"
                      >{{
                        errors.first('recipientAddress.addressPostal')
                      }}</span
                    >
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.recipientAddressCity')"
                      v-model="form.recipient.address.addressCity"
                      class="pr-5 w-full"
                      name="recipientAddress.addressCity"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('recipientAddress.addressCity')"
                      >{{ errors.first('recipientAddress.addressCity') }}</span
                    >
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <label class="text-sm opacity-75">
                      Recipient Address State
                    </label>
                    <v-select
                      :options="states"
                      :reduce="(myState) => myState.code"
                      v-if="form.recipient.address.addressCountryCode == 'MY'"
                      v-model="form.recipient.address.addressState"
                      class="pr-5 w-full"
                      name="recipientAddress.addressState"
                    />
                    <!-- to display text for Other Countries -->
                    <vs-input
                      v-model="form.recipient.address.addressState"
                      v-if="form.recipient.address.addressCountryCode != 'MY'"
                      class="pr-5 w-full"
                      name="recipientAddress.addressState"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('recipientAddress.addressState')"
                      >{{ errors.first('recipientAddress.addressState') }}</span
                    >
                  </div>
                  <div class="mt-3 w-9/12 md:w-1/2 sm:w-1/2">
                    <label class="text-sm opacity-75">
                      Recipient Address Country
                    </label>
                    <v-select
                      :options="countries"
                      label="label"
                      name="country"
                      :reduce="(country) => country.code"
                      v-model="form.recipient.address.addressCountryCode"
                      class="pr-5 w-full"
                      @option:selected="stateUpdate('recipient')"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('recipientAddress.addressCountryCode')"
                      >{{
                        errors.first('recipientAddress.addressCountryCode')
                      }}</span
                    >
                  </div>
                  <!--            End of Address Section-->
                </div>
              </vx-card>
            </div>
          </v-expand-transition>
        </vx-card>

        <!-- Tax Summary -->
        <vx-card class="mt-base" no-shadow card-border>
          <v-card-actions>
            <v-btn
              color="orange-lighten-2"
              variant="text"
              @click="showTaxDetail = !showTaxDetail"
            >
              {{ showTaxDetail ? $t('dashboard.hideTaxDetails') : $t('dashboard.expandTaxDetails') }}
            </v-btn>
          </v-card-actions>

          <v-expand-transition>
            <div v-show="showTaxDetail">
              <v-divider></v-divider>
              <vx-card class="mt-base" :title="$t('dashboard.taxInfo')" no-shadow card-border>
                <vs-table
                  stripe
                  ref="table"
                  :data="form.taxDetails"
                  @select="onSelect"
                  :noDataText="$t('dashboard.noDataAvailable')"
                >
                  <template slot="thead">
                    <vs-th>{{ $t('labels.type') }}</vs-th>
                    <vs-th>{{ $t('dashboard.totalTaxAmountPerTaxType') }}</vs-th>
                    <vs-th>{{ $t('dashboard.totalTaxableAmountPerTaxType') }}</vs-th>
                    <vs-th v-if="!isSgPeppolEnabled">{{ $t('dashboard.totalExemptionAmount') }}</vs-th>
                  </template>
                  <template slot-scope="{ data }">
                    <tbody>
                      <vs-tr
                        :data="tr"
                        :key="indextr"
                        v-for="(tr, indextr) in data"
                      >
                        <vs-td>
                          <p>{{ getTaxTypeLabel(tr.type) }}</p>
                        </vs-td>
                        <vs-td>
                          <p>{{ tr.totalAmountPerTaxType || '' }}</p>
                        </vs-td>
                        <vs-td>
                          <p>
                            {{
                              tr.type !== 'E'
                                ? tr.totalTaxableAmountPerTaxType
                                : ''
                            }}
                          </p>
                        </vs-td>
                        <vs-td v-if="!isSgPeppolEnabled">
                          <p>
                            {{
                              tr.type === 'E'
                                ? tr.totalTaxableAmountPerTaxType
                                : ''
                            }}
                          </p>
                        </vs-td>
                      </vs-tr>
                    </tbody>
                  </template>
                </vs-table>

                <div
                  v-if="!isSgPeppolEnabled && isTaxExemptionReasonEditable"
                  class="pt-3 pr-3 pl-3 vx-row"
                >
                  <div class="w-full">
                    <vs-input
                      :label="$t('dashboard.taxExemptionReason')"
                      v-model="form.taxExemptionReason"
                      class="pr-5 w-full"
                      name="taxExemptionReason"
                    />
                  </div>
                </div>
              </vx-card>
            </div>
          </v-expand-transition>
        </vx-card>

        <!-- Custom fields Info (Malaysia customs / incoterms — not in PINT-SG) -->
        <vx-card
          v-if="!isSgPeppolEnabled"
          class="mt-base"
          :title="$t('dashboard.customsFormDetails')"
          no-shadow
          card-border
        >
          <v-card-actions>
            <v-btn
              color="orange-lighten-2"
              variant="text"
              @click="showCustomForm = !showCustomForm"
            >
              {{
                showCustomForm
                  ? $t('dashboard.hideCustomFormDetails')
                  : $t('dashboard.expandCustomFormDetails')
              }}
            </v-btn>
          </v-card-actions>

          <v-expand-transition>
            <div v-show="showCustomForm">
              <v-divider></v-divider>
              <vx-card
                class="mt-base"
                :title="$t('dashboard.customForm')"
                no-shadow
                card-border
              >
                <div class="pr-3 pl-3 vx-row">
                  <div class="w-full md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.customsReferenceNum')"
                      v-model="form.additionalDetails.customsReferenceNum"
                      class="pr-5 w-full"
                      name="customsReferenceNum"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('customsReferenceNum')"
                    >
                      {{ errors.first('customsReferenceNum') }}
                    </span>
                  </div>
                  <div class="w-full md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.customsReferenceNum2')"
                      v-model="form.additionalDetails.customs2ReferenceNum"
                      class="pr-5 w-full"
                      name="customs2ReferenceNum"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('customs2ReferenceNum')"
                    >
                      {{ errors.first('customs2ReferenceNum') }}
                    </span>
                  </div>
                  <div class="w-full md:w-1/2 sm:w-1/2">
                    <vs-input
                      :label="$t('dashboard.incoterms')"
                      v-model="form.additionalDetails.incoterms"
                      class="pr-5 w-full"
                      name="incoterms"
                    />
                    <span
                      class="text-sm text-danger"
                      v-show="errors.has('incoterms')"
                    >
                      {{ errors.first('incoterms') }}
                    </span>
                  </div>
                </div>
              </vx-card>
            </div>
          </v-expand-transition>
        </vx-card>

        <!-- Items Info -->
        <vx-card class="mt-base" no-shadow card-border>
          <h4 class="mb-3 card-title">
            {{ $t('dashboard.invoiceItems') }} ({{ form.items ? form.items.length : '0' }})
          </h4>
          <vs-table
            pagination
            max-items="20"
            stripe
            ref="table"
            :data="form.items"
            @select="onSelect"
            :noDataText="$t('dashboard.noDataAvailable')"
          >
            <template slot="thead">
              <vs-th sort-key="item">{{ $t('dashboard.item') }}</vs-th>
              <vs-th sort-key="description">{{ $t('dashboard.description') }}</vs-th>
              <vs-th sort-key="classification" v-if="!isSgPeppolEnabled">{{ $t('dashboard.classification') }}</vs-th>
              <vs-th sort-key="quantity">{{ $t('dashboard.quantity') }}</vs-th>
              <vs-th sort-key="uom">{{ $t('dashboard.uom') }}</vs-th>
              <vs-th sort-key="unitCost">{{ $t('dashboard.unitCost') }}</vs-th>
              <vs-th sort-key="taxType">{{ $t('dashboard.taxTypeHeader') }}</vs-th>
              <vs-th sort-key="taxRate">{{ $t('dashboard.taxRate') }}</vs-th>
              <vs-th sort-key="tax">{{ $t('dashboard.tax') }}</vs-th>
              <vs-th sort-key="discountRate">{{ $t('dashboard.discountRate') }}</vs-th>
              <vs-th sort-key="discount">{{ $t('dashboard.discount') }}</vs-th>
              <vs-th sort-key="subTotalWithoutTax">{{ $t('dashboard.subtotal') }}</vs-th>
              <vs-th sort-key="subTotal">{{ $t('dashboard.total') }}</vs-th>
              <!-- <vs-th v-if="form.taxDetails.type === 'E'" sort-key="taxExemptionReason">Tax Exemption Reason</vs-th>
            <vs-th v-if="form.taxDetails.type === 'E'" sort-key="taxExemptionAmount">Tax Exemption Amount</vs-th> -->
              <vs-th>{{ $t('dashboard.actions') }}</vs-th>
            </template>
            <template slot-scope="{ data }">
              <tbody>
                <template v-for="(tr, indextr) in data">
                <vs-tr
                  :key="indextr"
                  :data="tr"
                  class="selected activeEdit"
                >
                  <vs-td>
                    <p>{{ tr.item }}</p>
                  </vs-td>
                  <vs-td>
                    <p>
                      <span v-if="!tr.description">❗</span>{{ tr.description }}
                    </p>
                  </vs-td>
                  <vs-td v-if="!isSgPeppolEnabled">
                    <p>
                      <span v-if="!tr.classification">❗Missing</span
                      >{{ tr.classification }}
                    </p>
                  </vs-td>
                  <vs-td>
                    <p>{{ tr.quantity }}</p>
                  </vs-td>
                  <vs-td>
                    <p>{{ tr.uom }}</p>
                  </vs-td>
                  <vs-td>
                    <p>
                      {{
                        (tr.unitCost ? tr.unitCost : 0)
                          | toCurrency(form.currency ? form.currency : '')
                      }}
                    </p>
                  </vs-td>
                <vs-td>
                  <div class="flex gap-2 items-center">
                    <span v-if="!tr.consolidatedTaxDetails || tr.consolidatedTaxDetails.length <= 1">{{ isSgPeppolEnabled ? getTaxTypeLabel(tr.taxType) : tr.taxType }}</span>
                    <span
                      v-else
                      class="cursor-pointer text-primary"
                      @click.stop="toggleRowExpand(tr)"
                      :title="tr.expandedRow ? $t('dashboard.hideTaxDetailsTooltip') : $t('dashboard.showTaxDetails')"
                    >
                      {{ tr.expandedRow ? '-' : '+' }}
                    </span>
                  </div>
                </vs-td>
                  <vs-td>
                    <p>{{ tr.taxRate }}</p>
                  </vs-td>
                  <vs-td>
                    <p>
                      {{
                        (tr.tax ? tr.tax : 0)
                          | toCurrency(form.currency ? form.currency : '')
                      }}
                    </p>
                  </vs-td>
                  <vs-td>
                    <p>
                      <span v-if="!tr.discountRate"></span
                      >{{ tr.discountRate ? tr.discountRate : 0 }} %
                    </p>
                  </vs-td>
                  <vs-td>
                    <p>
                      {{
                        (tr.discount ? tr.discount : 0)
                          | toCurrency(form.currency ? form.currency : '')
                      }}
                    </p>
                  </vs-td>
                  <vs-td>
                    <p>
                      {{
                        (tr.subTotalWithoutTax ? tr.subTotalWithoutTax : '')
                          | toCurrency(form.currency ? form.currency : '')
                      }}
                    </p>
                  </vs-td>
                  <vs-td>
                    <p>
                      {{
                        (tr.total ? tr.total : '')
                          | toCurrency(form.currency ? form.currency : '')
                      }}
                    </p>
                  </vs-td>
                  <vs-td>
                    <vs-button
                      :disabled="!isReadOnlyStatus(form.eInvoice.status)"
                      @click="editItem(tr)"
                      >{{ $t('dashboard.edit') }}</vs-button
                    >
                  </vs-td>
                </vs-tr>
                <!-- EXPANDABLE TAX DETAILS ROW -->
                <vs-tr
                  v-if="tr.expandedRow"
                  class="bg-gray-100"
                  :key="'expanded-' + indextr"
                >
                  <vs-td :colspan="isSgPeppolEnabled ? 13 : 14">
                    <div class="p-3">
                      <strong>Tax Breakdown:</strong>

                      <vs-table
                        :data="tr.consolidatedTaxDetails"
                        stripe
                        class="mt-2"
                        :noDataText="$t('dashboard.noDataAvailable')"
                      >
                        <template slot="thead">
                          <vs-th>{{ $t('dashboard.taxTypeHeader') }}</vs-th>
                          <vs-th>{{ $t('dashboard.taxableAmount') }}</vs-th>
                          <vs-th>{{ $t('dashboard.taxAmount') }}</vs-th>
                          <vs-th v-if="!isSgPeppolEnabled">{{ $t('dashboard.reason') }}</vs-th>
                        </template>

                        <template slot-scope="{ data }">
                          <tbody>
                            <vs-tr v-for="(tax, i) in data" :key="i">
                              <vs-td>{{ isSgPeppolEnabled ? getTaxTypeLabel(tax.type) : tax.type }}</vs-td>
                              <vs-td>{{ tax.totalTaxableAmountPerTaxType }}</vs-td>
                              <vs-td>{{ tax.totalAmountPerTaxType }}</vs-td>
                              <vs-td v-if="!isSgPeppolEnabled">{{ tax.taxExemptionReason || '-' }}</vs-td>
                            </vs-tr>
                          </tbody>
                        </template>
                      </vs-table>
                    </div>
                  </vs-td>
                </vs-tr>
                </template>
              </tbody>
            </template>
          </vs-table>

          <e-invoice-submissions-section
            v-if="form.docNo && form.eInvoice.type"
            :doc-no="form.docNo"
            :doc-type="form.eInvoice.type"
            :is-sg-peppol="isSgPeppolEnabled"
          />
          <e-invoice-audit-trail-section
            v-if="form.docNo"
            :doc-no="form.docNo"
            :doc-type="form.type"
          />
        </vx-card>

        <div class="flex items-start justify-end mt-6 w-full">
          <div class="flex-shrink-0">
            <vs-button
              :disabled="!isReadOnlyStatus(form.eInvoice.status) || (contactErrorSummary && contactErrorSummary.length > 0)"
              elevated
              class="mr-3"
              @click="save"
              >{{ $t('actions.save').toUpperCase() }}</vs-button
            >
          </div>

        </div>
      </div>
    </vx-card>

    <vs-sidebar
      click-not-close
      position-right
      parent="body"
      default-index="1"
      color="primary"
      class="add-new-data-sidebar items-no-padding"
      spacer
      v-model="sidebarVisible"
      style="width: 600px; max-width: 90vw"
    >
      <div class="flex justify-between items-center px-6 mt-6">
        <h5>{{ $t('dashboard.editLineItem').toUpperCase() }}</h5>
        <feather-icon
          icon="XIcon"
          @click.stop="closeSidebar"
          class="cursor-pointer"
        ></feather-icon>
      </div>
      <vs-divider class="mb-0"></vs-divider>
      <component
        :is="scrollbarTag"
        class="scroll-area--data-list-add-new"
        :settings="settings"
        :key="$vs.rtl"
      >
        <div class="p-6">
          <!-- Editable fields -->
          <vx-card class="mt-base" :title="$t('dashboard.editItem')" no-shadow card-border>
            <div class="pr-3 pl-3 vx-row">
              <div class="flex flex-col justify-end mt-1 mb-4 w-full" v-if="!isSgPeppolEnabled">
                <label for="classification" class="w-full"
                  >{{ $t('dashboard.classification') }}</label
                >
                <select
                  v-model="selectedItem.classification"
                  required
                  class="mt-1 w-full"
                >
                  <option
                    v-for="option in classifications"
                    :value="option.Code"
                    :key="option.Code"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </div>
              <div class="flex flex-col justify-end mt-1 mb-4 w-full">
                <label for="uom" class="w-full">{{ $t('dashboard.uom') }}</label>
                <vs-input v-model="selectedItem.uom" class="mt-1 w-full" />
              </div>
              <div
                class="flex flex-col justify-end mt-1 mb-4 w-full"
                v-if="!isSgPeppolEnabled && selectedItem.taxType === 'E'"
              >
                <label for="taxExemptionDetails" class="w-full"
                  >Tax Exemption Reason</label
                >
                <vs-textarea
                  v-model="selectedItem.taxExemptionDetails"
                  class="mt-1 w-full"
                  rows="5"
                />
              </div>
              <div
                class="flex flex-col justify-end mt-1 mb-4 w-full"
                v-if="!isSgPeppolEnabled && selectedItem.taxType === 'E'"
              >
                <label for="taxExemptionAmount" class="w-full"
                  >Tax Exemption Amount</label
                >
                <vs-input
                  v-model="selectedItem.taxExemptionAmount"
                  type="number"
                  class="mt-1 w-full"
                />
              </div>
            </div>
            <!-- Update and Cancel buttons -->
            <div class="flex justify-end mt-6 w-full">
              <vs-button class="mr-3 full-word" @click="updateItem"
                >Update</vs-button
              >
            </div>
            <div class="flex justify-end mt-6 w-full">
              <vs-button
                color="danger"
                class="mr-3 full-word"
                @click="closeSidebar"
                >Cancel</vs-button
              >
            </div>
          </vx-card>
        </div>
      </component>
    </vs-sidebar>
    <!-- IRBM Payload Info -->
    <transition name="fade">
      <div
        v-if="showIrbmPayload"
        class="modal-overlay"
        @click="showIrbmPayload = false"
      >
        <div class="modal-content" @click.stop>
          <vx-card :title="$t('dashboard.irbmInfo')" no-shadow card-border>
            <v-card-actions>
              <vs-button class="mr-3" @click="downloadEInvoicePayload"
                >DOWNLOAD PAYLOAD</vs-button
              >
            </v-card-actions>

            <vx-card>
              <div>
                <v-divider></v-divider>
                <div class="three-column-grid">
                  <div
                    v-for="(item, index) in invoiceGeneralInfo"
                    :key="index"
                    class="grid-item"
                  >
                    <label class="item-label">{{ item.label }}:</label>
                    <p class="item-value">{{ item.value || 'N/A' }}</p>
                  </div>
                </div>
                <!-- Invoice Line Items Table -->
                <h3 class="mb-4 text-lg font-semibold">{{ $t('dashboard.invoiceLineItems') }}</h3>
                <div class="overflow-x-auto">
                  <table class="min-w-full bg-white">
                    <thead class="bg-gray-100">
                      <tr>
                        <th class="px-4 py-2 text-left">{{ $t('dashboard.id') }}</th>
                        <th class="px-4 py-2 text-left">{{ $t('labels.name') }}</th>
                        <th class="px-4 py-2 text-left">{{ $t('dashboard.description') }}</th>
                        <th class="px-4 py-2 text-right">{{ $t('dashboard.quantity') }}</th>
                        <th class="px-4 py-2 text-left">{{ $t('dashboard.unit') }}</th>
                        <th class="px-4 py-2 text-right">{{ $t('labels.price') }}</th>
                        <th class="px-4 py-2 text-right">{{ $t('dashboard.lineTotal') }}</th>
                        <th class="px-4 py-2 text-right">{{ $t('dashboard.taxAmount') }}</th>
                        <th class="px-4 py-2 text-left">{{ $t('dashboard.taxCategory') }}</th>
                        <th class="px-4 py-2 text-right">{{ $t('dashboard.taxRate') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="item in invoiceLineItems"
                        :key="item.id"
                        class="border-b"
                      >
                        <td class="px-4 py-2">{{ item.id }}</td>
                        <td class="px-4 py-2">{{ item.name }}</td>
                        <td class="px-4 py-2">{{ item.description }}</td>
                        <td class="px-4 py-2 text-right">
                          {{ item.quantity }}
                        </td>
                        <td class="px-4 py-2">{{ item.unitCode }}</td>
                        <td class="px-4 py-2 text-right">{{ item.price }}</td>
                        <td class="px-4 py-2 text-right">
                          {{ item.lineExtensionAmount }}
                        </td>
                        <td class="px-4 py-2 text-right">
                          {{ item.taxAmount }}
                        </td>
                        <td class="px-4 py-2">{{ item.taxCategory }}</td>
                        <td class="px-4 py-2 text-right">
                          {{ item.taxPercent }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </vx-card>
            <vs-button @click="showIrbmPayload = false" class="mt-4"
              >Close</vs-button
            >
          </vx-card>
        </div>
      </div>
    </transition>
  </ValidationObserver>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import GenericUpload from '@/views/components/override/GenericUpload.vue'
import vSelect from 'vue-select'
import eInvoiceStatus from '@/mixins/e-invoice-status'
import eInvoiceType from '@/mixins/e-invoice-type'
import TransactionStatus from '@/mixins/transaction-status'
import VInput from '@/views/components/input/VInput.vue'
import { states } from '@/constants/states/MY'
import { countries } from '@/constants/countries'
import { uoms } from '@/constants/lhdn/unit-codes'
import { classifications } from '@/constants/lhdn/classification'
import { taxTypes } from '@/constants/lhdn/tax-types'
import { sgTaxCategories } from '@/constants/sg-peppol/tax-categories'
import { CustomerRegistrationTypes } from '@/constants/customer-registration-types'
import EInvoiceSubmissionsSection from './components/EInvoiceSubmissionsSection.vue'
import EInvoiceAuditTrailSection from './components/EInvoiceAuditTrailSection.vue'

export default {
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    VuePerfectScrollbar,
    GenericUpload,
    vSelect,
    VInput,
    ValidationObserver,
    ValidationProvider,
    EInvoiceSubmissionsSection,
    EInvoiceAuditTrailSection,
    countries,
    states,
    uoms,
    classifications,
    taxTypes,
    CustomerRegistrationTypes,
  },
  mixins: [eInvoiceStatus, TransactionStatus, eInvoiceType],
  data() {
    return {
      invalid: true,
      sidebarVisible: false,
      CustomerRegistrationTypes,
      selectedRow: {},
      selectedItem: {},
      serverError: [],
      supplierTin: null,
      isUpdate: false,
      isMounted: false,
      showSupplier: false,
      showBuyer: false,
      showRecipient: false,
      showTaxDetail: false,
      showCustomForm: false,
      showModal: false,
      showIrbmPayload: false,
      isDownloadingPdf: false,
      states,
      countries,
      uoms,
      classifications,
      taxTypes,
      isValidated: false,
      submissionId: '',
      irspSubmissionId: '',
      localValidationErrors: null,
      lhdnValidationErrors: null,
      irbmPayload: null,
      form: {
        items: [],
        taxDetails: [],
        linkedDocs: [],
        transactionsItems: [],
        supplier: {},
        buyer: {},
        recipient: {},
        eInvoice: {},
        additionalDetails: {
          customsReferenceNum: '',
          incoterms: '',
          customs2ReferenceNum: '',
        },
        taxExemptionReason: '',
      },
      settings: {
        maxScrollbarLength: 60,
        wheelSpeed: 0.6,
      },
    }
  },
  computed: {
    // Buyer section error summary
    buyerErrorSummary() {
      return this.validateContactInfo(this.form.buyer || {}, 'Buyer')
    },
    // Supplier section error summary
    supplierErrorSummary() {
      return this.validateContactInfo(this.form.supplier || {}, 'Supplier')
    },

    // Combined error summary for save button validation
    contactErrorSummary() {
      const messages = []

      if (this.isSgPeppolEnabled) {
        const sgIdentityMsg = this.validateSgPeppolIdentity()
        if (sgIdentityMsg) {
          messages.push(sgIdentityMsg)
        }
      }

      if (this.buyerErrorSummary) {
        messages.push(this.buyerErrorSummary)
      }
      if (this.supplierErrorSummary) {
        messages.push(this.supplierErrorSummary)
      }

      return messages.length ? messages : null
    },
    scrollbarTag() {
      return this.$store.getters.scrollbarTag
    },
    /**
     * Check if SG Peppol feature is enabled for the current tenant
     * Uses tenant store which is loaded on mount
     */
    isSgPeppolEnabled() {
      const tenant = this.$store.state.tenant?.tenant?.[0]
      return tenant?.enableSgPeppol === true
    },
    /**
     * Singapore Peppol (InvoiceNow) tenant settings. The SG supplier identity
     * (Peppol ID / UEN / GST registration) lives here — not on the LHDN-shaped
     * billing-doc supplier object, which only carries TIN/SST/MSIC.
     */
    sgPeppolSettings() {
      const tenant = this.$store.state.tenant?.tenant?.[0]
      return tenant?.sgPeppolSettings || {}
    },
    /**
     * Singapore UEN of the buyer, sourced from the linked customer record
     * (tenant_customers.sg_uen) — the SG counterpart of the LHDN buyer TIN.
     */
    buyerSgUen() {
      const raw = String(this.form?.customer?.sgUen || '').trim()
      if (!raw) return ''
      // Display bare UEN; customer.sg_uen may already carry a 0195: scheme prefix.
      return raw.replace(/^0195:/i, '')
    },
    /**
     * Buyer Peppol participant ID — must match api-core IrspUblJsonDto.getCustomerSgParticipantId().
     */
    buyerPeppolId() {
      const raw = String(this.form?.customer?.sgUen || '').trim()
      if (!raw) return ''
      const sgUen = raw.replace(/^0195:/i, '')
      return sgUen.toUpperCase().startsWith('SG')
        ? `0195:${sgUen}`
        : `0195:SGUEN${sgUen}`
    },
    /**
     * Colour for the buyer's Invoice Response (BIS Invoice Response 3) chip:
     * green for accepted/paid, red for rejected, amber for in-process/query.
     */
    buyerResponseColor() {
      const code = (this.form?.eInvoice?.irspBuyerResponseCode || '').toUpperCase()
      if (['AP', 'PD'].includes(code)) return 'success'
      if (code === 'RE') return 'danger'
      if (['IP', 'UQ', 'CA'].includes(code)) return 'warning'
      return 'primary'
    },
    /**
     * Check if document is valid to submit to IRSP (SG Peppol)
     * Must be validated and ready to submit (not already submitted successfully)
     */
    isValidToSubmitIrsp() {
      // Allow submission when status is 'ready to submit', 'valid', or 'accepted'
      const validStatuses = ['ready to submit', 'valid', 'accepted']
      const e = this.form?.eInvoice
      const isValidStatus = validStatuses.includes(e?.status?.toLowerCase())
      // The submission outcome lives directly on the e-invoice (there is no
      // `irspSubmission` relation on the API response). A document that already
      // has a non-failed IRSP outcome must not be submitted again; only
      // Error/Rejected results stay eligible for a retry.
      const blocked = ['Submitted', 'Processing', 'PartiallyCompleted', 'Completed']
      const alreadySubmitted =
        blocked.includes(e?.irspStatus) ||
        e?.irspPeppolStatus === 'SUCC' ||
        e?.irspIrasStatus === 'SUCC' ||
        (!!e?.irspSubmittedAt && !['Error', 'Rejected'].includes(e?.irspStatus))
      return isValidStatus && !alreadySubmitted
    },
    isTaxExemptionReasonEditable() {
      return this.form.taxDetails
        ? this.form.taxDetails.some((td) => td.type === 'E')
        : false
    },
    activeTenantInfo() {
      return this.$store.state.AppActiveTenant || []
    },
    invoiceGeneralInfo() {
      if (
        !this.irbmPayload ||
        !this.irbmPayload[0].Invoice ||
        !this.irbmPayload[0].Invoice[0]
      )
        return []

      const invoice = this.irbmPayload[0].Invoice[0]
      const supplier = invoice.AccountingSupplierParty[0].Party[0]
      const customer = invoice.AccountingCustomerParty[0].Party[0]
      const delivery = invoice.Delivery ? invoice.Delivery[0] : {}

      let data = [
        {
          label: 'Invoice ID',
          value: this.getNestedValue(invoice, ['ID', 0, '_']),
        },
        {
          label: 'Issue Date',
          value: this.getNestedValue(invoice, ['IssueDate', 0, '_']),
        },
        {
          label: 'Issue Time',
          value: this.getNestedValue(invoice, ['IssueTime', 0, '_']),
        },
        {
          label: 'Invoice Type Code',
          value: this.getNestedValue(invoice, ['InvoiceTypeCode', 0, '_']),
        },
        {
          label: 'Document Currency Code',
          value: this.getNestedValue(invoice, ['DocumentCurrencyCode', 0, '_']),
        },
        {
          label: 'Tax Currency Code',
          value: this.getNestedValue(invoice, ['TaxCurrencyCode', 0, '_']),
        },
        {
          label: 'Exchange Rate',
          value: this.getNestedValue(invoice, ['ExchangeRate', 0, '_']),
        },
        {
          label: 'Invoice Period Start Date',
          value: this.getNestedValue(invoice, [
            'InvoicePeriod',
            0,
            'StartDate',
            0,
            '_',
          ]),
        },
        {
          label: 'Invoice Period End Date',
          value: this.getNestedValue(invoice, [
            'InvoicePeriod',
            0,
            'EndDate',
            0,
            '_',
          ]),
        },
        {
          label: 'Invoice Period Description',
          value: this.getNestedValue(invoice, [
            'InvoicePeriod',
            0,
            'Description',
            0,
            '_',
          ]),
        },
        {
          label: 'Billing Reference ID',
          value: this.getNestedValue(invoice, [
            'BillingReference',
            0,
            'AdditionalDocumentReference',
            0,
            'ID',
            0,
            '_',
          ]),
        },
        {
          label: 'Customs Import Form ID',
          value: this.getNestedValue(invoice, [
            'AdditionalDocumentReference',
            0,
            'ID',
            0,
            '_',
          ]),
        },
        {
          label: 'Customs Import Form Type',
          value: this.getNestedValue(invoice, [
            'AdditionalDocumentReference',
            0,
            'DocumentType',
            0,
            '_',
          ]),
        },
        {
          label: 'K2 Document ID',
          value: this.getNestedValue(invoice, [
            'AdditionalDocumentReference',
            2,
            'ID',
            0,
            '_',
          ]),
        },
        {
          label: 'K2 Document Type',
          value: this.getNestedValue(invoice, [
            'AdditionalDocumentReference',
            2,
            'DocumentType',
            0,
            '_',
          ]),
        },
        {
          label: 'Incoterm',
          value: this.getNestedValue(invoice, [
            'AdditionalDocumentReference',
            3,
            'ID',
            0,
            '_',
          ]),
        },

        // Supplier Information
        {
          label: 'Supplier Additional Account ID',
          value: this.getNestedValue(invoice, [
            'AccountingSupplierParty',
            0,
            'AdditionalAccountID',
            0,
            '_',
          ]),
        },
        {
          label: 'Supplier Industry Classification',
          value: this.getNestedValue(supplier, [
            'IndustryClassificationCode',
            0,
            '_',
          ]),
        },
        {
          label: 'Supplier Industry Classification Name',
          value: this.getNestedValue(supplier, [
            'IndustryClassificationCode',
            0,
            'name',
          ]),
        },
        {
          label: 'Supplier TIN',
          value: this.getIdentificationValue(
            supplier.PartyIdentification,
            'TIN'
          ),
        },
        {
          label: 'Supplier BRN',
          value: this.getIdentificationValue(
            supplier.PartyIdentification,
            'BRN'
          ),
        },
        {
          label: 'Supplier SST',
          value: this.getIdentificationValue(
            supplier.PartyIdentification,
            'SST'
          ),
        },
        {
          label: 'Supplier TTX',
          value: this.getIdentificationValue(
            supplier.PartyIdentification,
            'TTX'
          ),
        },
        {
          label: 'Supplier City',
          value: this.getNestedValue(supplier, [
            'PostalAddress',
            0,
            'CityName',
            0,
            '_',
          ]),
        },
        {
          label: 'Supplier Postal Code',
          value: this.getNestedValue(supplier, [
            'PostalAddress',
            0,
            'PostalZone',
            0,
            '_',
          ]),
        },
        {
          label: 'Supplier Country Subentity Code',
          value: this.getNestedValue(supplier, [
            'PostalAddress',
            0,
            'CountrySubentityCode',
            0,
            '_',
          ]),
        },
        {
          label: 'Supplier Address Line 1',
          value: this.getNestedValue(supplier, [
            'PostalAddress',
            0,
            'AddressLine',
            0,
            'Line',
            0,
            '_',
          ]),
        },
        {
          label: 'Supplier Address Line 2',
          value: this.getNestedValue(supplier, [
            'PostalAddress',
            0,
            'AddressLine',
            1,
            'Line',
            0,
            '_',
          ]),
        },
        {
          label: 'Supplier Address Line 3',
          value: this.getNestedValue(supplier, [
            'PostalAddress',
            0,
            'AddressLine',
            2,
            'Line',
            0,
            '_',
          ]),
        },
        {
          label: 'Supplier Country Code',
          value: this.getNestedValue(supplier, [
            'PostalAddress',
            0,
            'Country',
            0,
            'IdentificationCode',
            0,
            '_',
          ]),
        },
        {
          label: 'Supplier Name',
          value: this.getNestedValue(supplier, [
            'PartyLegalEntity',
            0,
            'RegistrationName',
            0,
            '_',
          ]),
        },
        {
          label: 'Supplier Phone',
          value: this.getNestedValue(supplier, [
            'Contact',
            0,
            'Telephone',
            0,
            '_',
          ]),
        },
        {
          label: 'Supplier Email',
          value: this.getNestedValue(supplier, [
            'Contact',
            0,
            'ElectronicMail',
            0,
            '_',
          ]),
        },

        // Customer Information
        {
          label: 'Customer TIN',
          value: this.getIdentificationValue(
            customer.PartyIdentification,
            'TIN'
          ),
        },
        {
          label: 'Customer Registration',
          value: this.getIdentificationValue(
            customer.PartyIdentification,
            customer.registrationType
          ),
        },
        {
          label: 'Customer SST',
          value: this.getIdentificationValue(
            customer.PartyIdentification,
            'SST'
          ),
        },
        {
          label: 'Customer TTX',
          value: this.getIdentificationValue(
            customer.PartyIdentification,
            'TTX'
          ),
        },
        {
          label: 'Customer City',
          value: this.getNestedValue(customer, [
            'PostalAddress',
            0,
            'CityName',
            0,
            '_',
          ]),
        },
        {
          label: 'Customer Postal Code',
          value: this.getNestedValue(customer, [
            'PostalAddress',
            0,
            'PostalZone',
            0,
            '_',
          ]),
        },
        {
          label: 'Customer Country Subentity Code',
          value: this.getNestedValue(customer, [
            'PostalAddress',
            0,
            'CountrySubentityCode',
            0,
            '_',
          ]),
        },
        {
          label: 'Customer Address Line 1',
          value: this.getNestedValue(customer, [
            'PostalAddress',
            0,
            'AddressLine',
            0,
            'Line',
            0,
            '_',
          ]),
        },
        {
          label: 'Customer Address Line 2',
          value: this.getNestedValue(customer, [
            'PostalAddress',
            0,
            'AddressLine',
            1,
            'Line',
            0,
            '_',
          ]),
        },
        {
          label: 'Customer Address Line 3',
          value: this.getNestedValue(customer, [
            'PostalAddress',
            0,
            'AddressLine',
            2,
            'Line',
            0,
            '_',
          ]),
        },
        {
          label: 'Customer Country Code',
          value: this.getNestedValue(customer, [
            'PostalAddress',
            0,
            'Country',
            0,
            'IdentificationCode',
            0,
            '_',
          ]),
        },
        {
          label: 'Customer Name',
          value: this.getNestedValue(customer, [
            'PartyLegalEntity',
            0,
            'RegistrationName',
            0,
            '_',
          ]),
        },
        {
          label: 'Customer Phone',
          value: this.getNestedValue(customer, [
            'Contact',
            0,
            'Telephone',
            0,
            '_',
          ]),
        },
        {
          label: 'Customer Email',
          value: this.getNestedValue(customer, [
            'Contact',
            0,
            'ElectronicMail',
            0,
            '_',
          ]),
        },

        // Delivery Information
        {
          label: 'Recipient Name',
          value: this.getNestedValue(delivery, [
            'DeliveryParty',
            0,
            'PartyLegalEntity',
            0,
            'RegistrationName',
            0,
            '_',
          ]),
        },
        {
          label: 'Recipient City',
          value: this.getNestedValue(delivery, [
            'DeliveryParty',
            0,
            'PostalAddress',
            0,
            'CityName',
            0,
            '_',
          ]),
        },
        {
          label: 'Recipient Postal Code',
          value: this.getNestedValue(delivery, [
            'DeliveryParty',
            0,
            'PostalAddress',
            0,
            'PostalZone',
            0,
            '_',
          ]),
        },
        {
          label: 'Recipient Country Subentity Code',
          value: this.getNestedValue(delivery, [
            'DeliveryParty',
            0,
            'PostalAddress',
            0,
            'CountrySubentityCode',
            0,
            '_',
          ]),
        },
        {
          label: 'Recipient Address Line 1',
          value: this.getNestedValue(delivery, [
            'DeliveryParty',
            0,
            'PostalAddress',
            0,
            'AddressLine',
            0,
            'Line',
            0,
            '_',
          ]),
        },
        {
          label: 'Recipient Address Line 2',
          value: this.getNestedValue(delivery, [
            'DeliveryParty',
            0,
            'PostalAddress',
            0,
            'AddressLine',
            1,
            'Line',
            0,
            '_',
          ]),
        },
        {
          label: 'Recipient Address Line 3',
          value: this.getNestedValue(delivery, [
            'DeliveryParty',
            0,
            'PostalAddress',
            0,
            'AddressLine',
            2,
            'Line',
            0,
            '_',
          ]),
        },
        {
          label: 'Recipient Country Code',
          value: this.getNestedValue(delivery, [
            'DeliveryParty',
            0,
            'PostalAddress',
            0,
            'Country',
            0,
            'IdentificationCode',
            0,
            '_',
          ]),
        },
        {
          label: 'Recipient TIN',
          value: this.getIdentificationValue(
            delivery.DeliveryParty &&
              delivery.DeliveryParty[0] &&
              delivery.DeliveryParty[0].PartyIdentification,
            'TIN'
          ),
        },
        {
          label: 'Recipient BRN',
          value: this.getIdentificationValue(
            delivery.DeliveryParty &&
              delivery.DeliveryParty[0] &&
              delivery.DeliveryParty[0].PartyIdentification,
            'BRN'
          ),
        },
        {
          label: 'Shipment ID',
          value: this.getNestedValue(delivery, ['Shipment', 0, 'ID', 0, '_']),
        },
        {
          label: 'Freight Charge',
          value: this.getNestedValue(delivery, [
            'Shipment',
            0,
            'FreightAllowanceCharge',
            0,
            'Amount',
            0,
            '_',
          ]),
        },

        // Payment Information
        {
          label: 'Payment Means Code',
          value: this.getNestedValue(invoice, [
            'PaymentMeans',
            0,
            'PaymentMeansCode',
            0,
            '_',
          ]),
        },
        {
          label: 'Payment Account ID',
          value: this.getNestedValue(invoice, [
            'PaymentMeans',
            0,
            'PayeeFinancialAccount',
            0,
            'ID',
            0,
            '_',
          ]),
        },

        // Total Amount
        {
          label: 'Total Amount',
          value: this.getNestedValue(invoice, [
            'LegalMonetaryTotal',
            0,
            'PayableAmount',
            0,
            '_',
          ]),
        },
      ]

      return data.filter((item) => item.value !== undefined)
    },
    invoiceLineItems() {
      if (
        !this.irbmPayload ||
        !this.irbmPayload[0].Invoice ||
        !this.irbmPayload[0].Invoice[0]
      )
        return []

      const invoice = this.irbmPayload[0].Invoice[0]
      if (!invoice.InvoiceLine || !Array.isArray(invoice.InvoiceLine)) return []
      return invoice.InvoiceLine.map((line, index) => ({
        id: this.getNestedValue(line, ['ID', 0, '_']),
        name: this.getNestedValue(line, ['Item', 0, 'Name', 0, '_']),
        description: this.getNestedValue(line, [
          'Item',
          0,
          'Description',
          0,
          '_',
        ]),
        quantity: this.getNestedValue(line, ['InvoicedQuantity', 0, '_']),
        unitCode: this.getNestedValue(line, [
          'InvoicedQuantity',
          0,
          'unitCode',
        ]),
        price: this.getNestedValue(line, ['Price', 0, 'PriceAmount', 0, '_']),
        lineExtensionAmount: this.getNestedValue(line, [
          'LineExtensionAmount',
          0,
          '_',
        ]),
        taxAmount: this.getNestedValue(line, [
          'TaxTotal',
          0,
          'TaxAmount',
          0,
          '_',
        ]),
        taxCategory: this.getNestedValue(line, [
          'TaxTotal',
          0,
          'TaxSubtotal',
          0,
          'TaxCategory',
          0,
          'ID',
          0,
          '_',
        ]),
        taxPercent: this.getNestedValue(line, [
          'TaxTotal',
          0,
          'TaxSubtotal',
          0,
          'TaxCategory',
          0,
          'Percent',
          0,
          '_',
        ]),
      }))
    },
  },
  async mounted() {
    this.isMounted = true
    this.initializeForm()

    // Load tenant data to check enableSgPeppol setting
    try {
      await this.$store.dispatch('tenant/fetchTenant')
    } catch (e) {
      console.error('Failed to load tenant data:', e)
    }
  },
  created() {
    this.isUpdate =
      this.$route.params.billingDocNo || Object.entries(this.data).length > 0
    const docNo = this.$route.params.billingDocNo
      ? this.$route.params.billingDocNo
      : this.data.docNo
    if (this.isUpdate) this.fetchRecord(docNo)
  },
  watch: {
    showIrbmPayload(newVal) {
      if (newVal) {
        this.generateUblPayload()
      }
    },
  },
  methods: {
    /**
     * Validates contact information for an entity (buyer, supplier, etc.)
     * @param {Object} entity - The entity object to validate
     * @param {String} entityName - The name of the entity (e.g., 'Buyer', 'Supplier')
     * @returns {String|null} - Comma-separated error messages or null if no errors
     */
    /**
     * SG Peppol (InvoiceNow) identity checks aligned with IrspUblJsonDto.validate().
     * LHDN TIN/SST/MSIC are intentionally excluded — those fields are hidden for SG tenants.
     */
    validateSgPeppolIdentity() {
      if (!this.isSgPeppolEnabled) return null
      const messages = []
      const settings = this.sgPeppolSettings || {}
      if (!settings.peppolId) {
        messages.push('Tenant Peppol ID is required (SG Peppol settings)')
      }
      if (!settings.uen) {
        messages.push('Tenant Singapore UEN is required (SG Peppol settings)')
      }
      if (!settings.gstRegistrationNo) {
        messages.push('Tenant GST registration number is required (SG Peppol settings)')
      }
      if (!this.buyerSgUen) {
        messages.push('Customer SG UEN is required (customer record)')
      }
      return messages.length ? messages.join(', ') : null
    },
    validateContactInfo(entity, entityName) {
      const validationRules = [
        { path: ['name'], message: `${entityName} name is required` },
        { path: ['email'], message: `${entityName} email is required` },
        { path: ['contact'], message: `${entityName} contact number is required` },
        { path: ['address', 'addressNum'], message: `${entityName} address number is required` },
        { path: ['address', 'addressPostal'], message: `${entityName} postal code is required` },
        { path: ['address', 'addressCity'], message: `${entityName} city is required` },
        { path: ['address', 'addressState'], message: `${entityName} state is required` },
        { path: ['address', 'addressCountryCode'], message: `${entityName} country is required` },
      ]

      // Malaysia LHDN identifies parties by TIN + registration number. Singapore
      // Peppol (InvoiceNow) does not use a TIN — the participant is identified by
      // Peppol ID / UEN — so these LHDN-only fields must not block SG documents.
      if (!this.isSgPeppolEnabled) {
        validationRules.push(
          { path: ['tin'], message: `${entityName} TIN is required` },
          { path: ['registration'], message: `${entityName} registration number is required` },
        )
      }

      const errors = validationRules
        .filter(rule => !this.getNestedValue(entity, rule.path))
        .map(rule => rule.message)

      return errors.length ? errors.join(', ') : null
    },
    getLocalizedEInvoiceStatus(status) {
      if (!status) return ''
      const statusMap = {
        'ready to submit': 'readyToSubmit',
        'readytosubmit': 'readyToSubmit',
        'missing info': 'missingInfo',
        'missinginfo': 'missingInfo',
        submitted: 'submitted',
        valid: 'valid',
        invalid: 'invalid',
        rejected: 'rejected',
        cancelled: 'cancelled',
        accepted: 'accepted',
        error: 'error',
        consolidated: 'consolidated',
      }
      const normalized = (status || '').toLowerCase().trim()
      const normalizedNoSpaces = normalized.replace(/\s+/g, '')
      const mappedKey = statusMap[normalized] || statusMap[normalizedNoSpaces]
      return mappedKey && this.$te(`dashboard.${mappedKey}`) ? this.$t(`dashboard.${mappedKey}`) : status
    },
    toggleRowExpand(tr) {
      tr.expandedRow = !tr.expandedRow;
      },
    buyerRegistrationTypeUpdate(event) {
      this.form.buyer.registrationType = event.label.toString()
    },
    supplierRegistrationTypeUpdate(event) {
      this.form.supplier.registrationType = event.label.toString()
    },
    async searchTin(type) {
      const formData = type === 'supplier' ? this.form.supplier : this.form.buyer;

      // Check if registration number exists
      if (!formData?.registration) {
        this.$notify.warning(
          'TIN Search',
          'Please enter a valid registration number before searching for TIN.'
        );
        return;
      }

      try {
        this.verifying = true;

        const res = await this.$store.dispatch('tenantCustomers/searchTin', {
          identificationNumber: formData.registration,
          registrationType: formData.registrationType,
        });

        // Safely extract TIN from response
        const tin = res?.tin ? String(res.tin).trim() : '';

        if (!tin) {
          // No TIN returned -> use fallback logic
          //formData.tin = this.getFallbackTin(this.form.type, this.form.country);
          this.$notify.warning(
            'TIN Search',
            'No TIN found for the provided registration number.'
          );
        } else {
          formData.tin = tin;
        }
      } catch (error) {
        // API error -> still use fallback logic
        //formData.tin = this.getFallbackTin(this.form.type, this.form.country);
        this.$notify.error(
          'TIN Search',
          'An error occurred while searching for TIN. Please try again later.'
        );
      } finally {
        this.verifying = false;
      }
    },
    getNestedValue(obj, path) {
      return path.reduce(
        (xs, x) => (xs && xs[x] !== undefined ? xs[x] : undefined),
        obj
      )
    },
    getIdentificationValue(identifications, schemeID) {
      if (!identifications) return undefined
      const item = identifications.find(
        (id) => id && id.ID && id.ID[0] && id.ID[0].schemeID === schemeID
      )
      return item && item.ID && item.ID[0] && item.ID[0]._
    },
    initializeForm() {
      this.form = {
        items: [],
        taxDetails: {
          type: '',
        },
        linkedDocs: [],
        transactionsItems: [],
        supplier: {},
        buyer: {},
        recipient: {},
        eInvoice: {},
        additionalDetails: this.form.additionalDetails || {
          customsReferenceNum: '',
          incoterms: '',
          customs2ReferenceNum: '',
        },
      }
    },
    async fetchRecord(docNo) {
      try {
        let response = await this.$store.dispatch(
          'billingDocs/fetchRecord',
          docNo
        )
        this.form = response.data
        this.form.taxDetails = Array.isArray(this.form.taxDetails)
          ? this.form.taxDetails
          : []
        this.findAndSetTaxExemptionReason()
        this.form.additionalDetails = this.form.additionalDetails || {
          customsReferenceNum: '',
          incoterms: '',
          customs2ReferenceNum: '',
        }
        this.form.items = Array.isArray(this.form.items)
        ? this.form.items.map(x => ({
            ...x,
            expandedRow: false
          }))
        : [];
        const status = this.form.eInvoice.status.toUpperCase()
        this.isValidated = status === 'VALID' || status === 'ACCEPTED'
        const validationErr = this.form.eInvoice.errors
        if (validationErr && validationErr.validationResults) {
          this.lhdnValidationErrors =
            validationErr.validationResults.validationSteps.filter(function (
              vErr
            ) {
              return vErr.status === 'Invalid'
            })
          console.log(
            `this.lhdnValidationErrors - ${JSON.stringify(
              this.lhdnValidationErrors
            )}`
          )
          // this.lhdnValidationErrors = JSON.stringify(errList).replace(/,/gm,`,\n`)
        } else if (validationErr) {
          let temp = null
          if (typeof validationErr === 'object') {
            if (validationErr.ApplicationError)
              temp = validationErr.ApplicationError
            else temp = JSON.parse(JSON.stringify(validationErr))
          } else temp = JSON.parse(validationErr).ApplicationError

          console.log(`this.lhdnValidationErrors - ${JSON.stringify(temp)}`)
          if (temp)
            this.localValidationErrors =
              temp && temp.ApplicationError
                ? temp.ApplicationError
                : temp.details
                ? temp.details
                : temp
        }

        const isValid =
          (await this.$refs.observer.validate()) &&
          (await this.$validator.validateAll())
        if (isValid) this.invalid = false
      } catch (err) {
        console.error(err)
      }
    },
    async generateUblPayload() {
      try {
        let response = await this.$store.dispatch(
          'lhdnSubmissions/generateUblPayload',
          [{ docNo: this.form.docNo, type: this.form.type }]
        )
        this.irbmPayload = response.data

        console.log(`irbmPayload - ${JSON.stringify(this.irbmPayload)}`)
      } catch (err) {
        console.error(err)
      }
    },
    async validateAndSubmit(item) {
      const isValid = await this.$validator.validateAll() // await this.$refs.observer.validate()
      if (!isValid) {
        this.invalid = true
        return
      } else {
        this.invalid = false
        item.eInvoice.autoSubmitted = false
        await this.$store
          .dispatch('billingDocs/addRecord', item)
          .then((resp) => {
            if (resp.status == 200 || resp.status == 201) {
              this.$notify.updated(
                'Invoice',
                `Invoice ${this.form.docNo} has been updated successfully`
              )
              this.fetchRecord(this.form.docNo)
            }
          })
          .catch((err) => console.error(err))
      }
    },
    onSelect(rows) {
      this.selectedRow = rows[0] // Assuming single row selection
      this.selectedItem = { ...rows[0] } // Make a shallow copy
      this.selectedItemIndex = this.form.items.indexOf(this.selectedRow) // Get index of the selected row
      this.sidebarVisible = true
    },
    editItem(item) {
      this.selectedItem = { ...item } // Make a shallow copy
      this.selectedItemIndex = this.form.items.indexOf(item) // Get index of the item
      this.sidebarVisible = true
    },
    closeSidebar() {
      this.sidebarVisible = false
    },
    async updateItem() {
      this.$set(this.form.items, this.selectedItemIndex, {
        ...this.selectedItem,
      })
      this.closeSidebar()
      await this.validateAndSubmit(this.form)
    },
    async save() {
      this.findTaxExemptionReasonAndUpdateForm()
      await this.validateAndSubmit(this.form)
    },
    async submitToLhdn() {
      try {
        let payload = { tenantId: '', billingDocs: [] }
        payload.tenantId = this.activeTenant
        payload['billingDocs'].push({
          docNo: this.form.docNo,
          type: this.form.type,
        })
        const response = await this.$store.dispatch(
          'einvoice/submitLhdn',
          payload
        )
        if (response) {
          if (response.status < 200 || response.status > 299) {
            this.$notify.error(
              this.$t('invoice.message.FailedTitle'),
              response.data
            )
            throw 'processing error, please try again later.'
          } else {
            /* this.submissionId = response.data.uuid
            this.showSendToValidateSuccess() */
            this.showValidatedSuccess()
          }
        }
      } catch (err) {
        this.$notify.error(
          this.$t('invoice.message.FailedTitle'),
          err.data?.message || 'An error occurred'
        )
      }
    },
    /**
     * Submit document to SG Peppol (IRSP)
     */
    async submitToIrsp() {
      try {
        const payload = {
          billingDocs: [{ docNo: this.form.docNo, type: this.form.type }],
        }
        const response = await this.$store.dispatch('einvoice/submitIrsp', payload)

        if (response && response.data) {
          this.irspSubmissionId = response.data.uuid
          this.$vs.notify({
            color: 'success',
            title: 'SG Peppol Submission Created',
            text: `Document ${this.form.docNo} has been submitted to SG Peppol`,
          })
          this.showIrspSubmissionSuccess()
        }
      } catch (err) {
        this.$vs.notify({
          color: 'danger',
          title: 'SG Peppol Submission Failed',
          text: err?.response?.data?.message || err?.message || 'An error occurred',
        })
      }
    },
    showSendToValidateSuccess() {
      if (this.submissionId)
        this.$router
          .push('/lhdn-submissions/' + this.submissionId)
          .catch(() => {})
    },
    /**
     * Navigate to IRSP submission detail page after successful submission
     */
    showIrspSubmissionSuccess() {
      if (this.irspSubmissionId) {
        this.$router
          .push('/sg-peppol-submissions/' + this.irspSubmissionId)
          .catch(() => {})
      }
    },
    showValidatedSuccess() {
      this.$notify.success(
        'Invoice Has Been Submitted',
        'Document has been submitted to LHDN successfully'
      )
      this.fetchRecord(this.form.docNo)
    },
    async downloadEInvoicePdf() {
      try {
        this.isDownloadingPdf = true
        let response = await this.$store
          .dispatch('einvoice/generateEInvoicePdf', this.form)
        this.form.eInvoice.generatedFileUrl = response.data.generatedFileUrl
        window.open(this.form.eInvoice.generatedFileUrl, '_blank')
        this.$notify.success(
          'E-Invoice PDF',
          'E-Invoice PDF generated and downloaded successfully'
        )
      } catch (err) {
        console.error(err)
        this.$notify.error(
          'E-Invoice PDF Generation Failed',
          err?.response?.data?.message || err?.message || 'An error occurred while generating the PDF'
        )
      } finally {
        this.isDownloadingPdf = false
      }
    },
    async downloadEInvoicePayload() {
      let response = await this.$store
        .dispatch('einvoice/getPayload', this.form)
        .catch((err) => console.error(err))
      this.form.eInvoice.payloadFileUrl = response.data.payloadFileUrl
      window.open(this.form.eInvoice.payloadFileUrl, '_blank')
    },
    onViewProForma(docNo) {
      this.$router.push('/pro-forma-invoices/' + docNo).catch(() => {})
    },
    onViewCnDn(docNo) {
      this.$router.push('/cn-dn/' + docNo).catch(() => {})
    },
    onViewPayment(id) {
      this.$router.push('/payment-transactions/' + id).catch(() => {})
    },
    onViewCustomerDetail(customerId) {
      this.$router.push('/customers/' + customerId).catch(() => {})
    },
    getTaxTypeLabel(type) {
      // SG Peppol tenants use Singapore GST categories (SR/ZR/ES33/...). For
      // unknown codes fall back to the raw code rather than a Malaysia label.
      // The Malaysia/LHDN branch below is unchanged.
      if (this.isSgPeppolEnabled) {
        const sgCategory = sgTaxCategories.find((t) => t.key === type)
        return sgCategory ? sgCategory.label : type || ''
      }
      const taxType = taxTypes.find((t) => t.key === type)
      return taxType ? taxType.label : ''
    },
    findAndSetTaxExemptionReason() {
      if (this.form.taxDetails) {
        const taxExempted = this.form.taxDetails.find((td) => td.type === 'E')
        if (taxExempted && taxExempted.taxExemptionReason) {
          this.form.taxExemptionReason = taxExempted.taxExemptionReason
        }
      }
    },
    findTaxExemptionReasonAndUpdateForm() {
      if (this.form.taxDetails) {
        const taxExempted = this.form.taxDetails.find((td) => td.type === 'E')
        if (taxExempted) {
          taxExempted.taxExemptionReason = this.form.taxExemptionReason
        }
      }
    },
    stateUpdate(str) {
      if (str == 'supplier') this.form.supplier.address.addressState = ''

      if (str == 'buyer') this.form.buyer.address.addressState = ''

      if (str == 'recipient') this.form.recipient.address.addressState = ''
    },
  },
}
</script>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
.vs-sidebar {
  z-index: 999999 !important;
}

.vs-sidebar-content {
  height: 100%;
  overflow-y: auto;
}
.modal-content {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  max-width: 90%;
  max-height: 90%;
  overflow-y: auto;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.three-column-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
}

.grid-item {
  margin-top: 0.75rem;
}

.item-label {
  font-size: 0.875rem;
  color: #000;
  font-weight: 600;
}

.item-value {
  margin-top: 0.25rem;
}

.table-header {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.table-container {
  overflow-x: auto;
}

table {
  min-width: 100%;
  background-color: white;

  th,
  td {
    padding: 0.5rem 1rem;
    text-align: left;
  }

  th {
    background-color: #f3f4f6;
    font-weight: 600;
  }

  tr {
    &:nth-child(even) {
      background-color: #f9fafb;
    }
  }
}

.scroll-area--data-list-add-new {
  max-height: calc(100vh - 50px);
  overflow-y: auto;
}

.flex {
  display: flex;
}

.flex-col {
  flex-direction: column;
}

.w-full {
  width: 100%;
}

.mt-1 {
  margin-top: 0.25rem;
}

.mt-6 {
  margin-top: 1.5rem;
}

.mb-4 {
  margin-bottom: 1rem;
}

.mr-3 {
  margin-right: 0.75rem;
}

.vs-textarea {
  resize: vertical; /* Allow vertical resizing */
}

.full-word {
  white-space: nowrap;
}

.space-between {
  margin-bottom: 20px;
}


.contact-error-alert .contact-error-list,
.contact-error-alert .contact-error-list li {
  color: hsl(8, 86%, 45%);
}

.contact-error-alert .contact-error-list {
  margin: 0;
  padding-left: 1rem;
}

.contact-error-alert .contact-error-list li {
  margin: 0.25rem 0;
}

.contact-error-alert {
  display: inline-block;
  max-width: 70%;
}

</style>
